import { forwardRef, useCallback, useEffect, useRef } from 'react'
import { Controller, FieldErrors, useFormContext } from 'react-hook-form'
import Input, { InputProps } from './Input'
import { AutocompleteDropdown } from './AutocompleteDropdown'
import {
  useAutocompleteKeyboard,
  useBetriebsstelleAutocomplete
} from '../../hooks'
import { ZUGMELDESTELLE_MAX_LENGTH } from '../../utils'
import { useBetriebsstelleSearchContext } from '../../contexts'
import { Betriebsstellenart } from '../../types'

export interface BetriebsstelleFieldProps extends Omit<InputProps, 'label'> {
  name?: string
  label: string
  errors?: FieldErrors
  typ?: Betriebsstellenart | ''
  activateEmptyWarning?: boolean
  'data-testid': string
  pattern?: string
  maxLength?: number
}

const BetriebsstelleField = forwardRef<
  HTMLInputElement,
  BetriebsstelleFieldProps
>(
  (
    {
      name,
      label,
      errors,
      typ,
      activateEmptyWarning = false,
      'data-testid': testId,
      variant = 'solid',
      className,
      pattern,
      maxLength,
      ...props
    },
    ref
  ) => {
    const { control } = useFormContext()
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Get search API from context - this enables autocomplete functionality
    // fdl-app: provides searchAPI -> autocomplete enabled
    // tf-app: no searchAPI -> fallback to simple input field
    const { searchAPI } = useBetriebsstelleSearchContext()

    const {
      filteredSuggestions,
      activeSuggestionIndex,
      setActiveSuggestionIndex,
      resetFilteredSuggestions,
      handleInputChange,
      isEnabled,
      openDropDown
    } = useBetriebsstelleAutocomplete({
      art: typ === '' ? null : (typ as Betriebsstellenart),
      searchAPI
    })

    const selectSuggestion = useCallback(
      (suggestion: string, onChange: (value: string) => void) => {
        // Update autocomplete hook
        handleInputChange(suggestion)

        // Update form value through Controller
        onChange(suggestion)

        // Close dropdown
        resetFilteredSuggestions()
      },
      [handleInputChange, resetFilteredSuggestions]
    )

    const handleControllerInputChange = useCallback(
      (
        e: React.ChangeEvent<HTMLInputElement>,
        onChange: (value: string) => void
      ) => {
        const newValue = e.target.value

        // Update autocomplete hook
        handleInputChange(newValue)

        // Update form value through Controller
        onChange(newValue)
      },
      [handleInputChange]
    )

    const onChangeRef = useRef<((value: string) => void) | null>(null)

    const { handleKeyDown } = useAutocompleteKeyboard({
      suggestions: filteredSuggestions,
      activeSuggestionIndex,
      setActiveSuggestionIndex,
      onSelectSuggestion: (suggestion: string) => {
        if (onChangeRef.current) {
          selectSuggestion(suggestion, onChangeRef.current)
        }
      },
      onReset: resetFilteredSuggestions,
      fieldName: name ?? ''
    })

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          resetFilteredSuggestions()
        }
      }

      document.addEventListener('click', handleClickOutside)
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }, [resetFilteredSuggestions])

    // Render autocomplete version if search API is available and enabled (fdl-app)
    if (name && testId && isEnabled) {
      return (
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, value } }) => {
            // Update the ref with the current onChange function
            onChangeRef.current = onChange

            return (
              <div
                className="relative"
                data-testid={`${testId}-wrapper`}
                ref={dropdownRef}
              >
                <Input
                  {...props}
                  name={name}
                  errors={errors}
                  type="text"
                  label={label}
                  variant={variant}
                  className={className}
                  aria-controls={`${name}-suggestions`}
                  aria-expanded={filteredSuggestions.length > 0}
                  aria-autocomplete="list"
                  aria-activedescendant={
                    activeSuggestionIndex >= 0
                      ? `${name}-suggestion-${activeSuggestionIndex}`
                      : undefined
                  }
                  value={value || ''}
                  onClick={() => value && openDropDown()}
                  onChange={(e) => handleControllerInputChange(e, onChange)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => {
                    onBlur()
                    resetFilteredSuggestions()
                  }}
                  autoComplete="off"
                  activateEmptyWarning={activateEmptyWarning}
                  data-testid={`${testId}-input`}
                  ref={ref}
                  pattern={pattern}
                  maxLength={maxLength}
                />
                {filteredSuggestions.length > 0 && (
                  <AutocompleteDropdown
                    fieldName={name}
                    testId={testId}
                    suggestions={filteredSuggestions}
                    activeSuggestionIndex={activeSuggestionIndex}
                    onSelectSuggestion={(suggestion) =>
                      selectSuggestion(suggestion, onChange)
                    }
                    setActiveSuggestionIndex={setActiveSuggestionIndex}
                    fieldValue={value}
                  />
                )}
              </div>
            )
          }}
        />
      )
    }

    // Fallback to simple input field when no search API is available (tf-app)
    if (name && control) {
      return (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Input
              {...props}
              {...field}
              name={name}
              maxLength={ZUGMELDESTELLE_MAX_LENGTH}
              ref={ref}
              label={label}
              variant={variant}
              errors={errors}
              activateEmptyWarning={activateEmptyWarning}
              data-testid={testId}
              className={className}
              pattern={pattern}
            />
          )}
        />
      )
    }

    // Fallback without form context
    return (
      <Input
        {...props}
        name={name}
        maxLength={ZUGMELDESTELLE_MAX_LENGTH}
        ref={ref}
        label={label}
        variant={variant}
        errors={errors}
        activateEmptyWarning={activateEmptyWarning}
        data-testid={testId}
        className={className}
        pattern={pattern}
      />
    )
  }
)

BetriebsstelleField.displayName = 'BetriebsstelleField'

export default BetriebsstelleField
